'use client';

import { useCallback, useState } from 'react';
import { toast } from 'react-toastify';

import { useAuth } from '@/hooks/useAuth';
import { usePolymarketClient } from '@/hooks/usePolymarketClient';
import type { TradingWalletKind } from '@/types/auth.types';
import { checkAllApprovals, createAllApprovalTxs } from '@/utils/approvals.utils';
import { executeRelayMetaTxBatch } from '@/utils/relayerBatch.utils';

import { MESSAGES } from '@/constants/messages.constants';

export interface ApprovalStatus {
  allApproved: boolean;
  usdcApprovals: Record<string, boolean>;
  outcomeTokenApprovals: Record<string, boolean>;
}

/**
 * useTokenApprovals
 *
 * Manages all required ERC-20 and ERC-1155 token approvals needed to
 * trade on Polymarket through the Gnosis Safe (proxy wallet).
 */
export default function useTokenApprovals() {
  const { user } = useAuth();
  const { client } = usePolymarketClient();
  const [isCheckingApprovals, setIsCheckingApprovals] = useState(false);
  const [isSettingApprovals, setIsSettingApprovals] = useState(false);
  const [approvalStatus, setApprovalStatus] = useState<ApprovalStatus | null>(null);
  const [approvalError, setApprovalError] = useState<string | null>(null);

  /**
   * Reads the on-chain approval state for the Safe's required token permissions.
   */
  const checkAllTokenApprovals = useCallback(
    async (safeAddress: string): Promise<ApprovalStatus> => {
      setIsCheckingApprovals(true);
      setApprovalError(null);
      try {
        const status = await checkAllApprovals(safeAddress);
        setApprovalStatus(status);
        return status;
      } catch (err) {
        const error = err instanceof Error ? err : new Error('Failed to check approvals');
        const msg = error.message;
        setApprovalError(msg);
        console.error('[TokenApprovals] Check failed:', msg);
        throw error;
      } finally {
        setIsCheckingApprovals(false);
      }
    },
    [],
  );

  /**
   * Executes all required token approvals as a batched meta-transaction
   */
  const setAllTokenApprovals = useCallback(
    async (
      relayClientOverride?: typeof client,
      tradingWalletKindOverride?: TradingWalletKind,
      funderAddressOverride?: string,
    ): Promise<boolean> => {
      const activeClient = relayClientOverride ?? client;
      if (!activeClient) {
        const msg = '[TokenApprovals] RelayClient not initialized.';
        console.error(msg);
        toast.error(MESSAGES.TOAST.TRADING_CLIENT_NOT_READY);
        return false;
      }

      const tradingKind = tradingWalletKindOverride ?? user?.tradingWalletKind;
      const funder = funderAddressOverride || user?.proxyWalletAddress;

      setIsSettingApprovals(true);
      setApprovalError(null);
      try {
        const approvalTxs = createAllApprovalTxs();
        const metaTxs = approvalTxs.map((txn) => ({
          to: txn.to,
          data: txn.data,
          value: txn.value ?? '0',
        }));

        if (!funder) {
          throw new Error('Trading wallet address not ready');
        }

        let response;
        let lastError;
        const MAX_ATTEMPTS = 10;
        for (let i = 0; i < MAX_ATTEMPTS; i++) {
          try {
            response = await executeRelayMetaTxBatch(
              activeClient as any,
              funder,
              tradingKind,
              metaTxs,
              'Set all token approvals for trading',
            );
            break; // Success!
          } catch (err: any) {
            lastError = err;
            const msg = typeof err === 'string' ? err : err?.message || JSON.stringify(err);
            if (
              msg.includes('not registered') ||
              msg.includes('wallet registry validation failed')
            ) {
              console.warn(
                `[TokenApprovals] Wallet not registered on relayer yet. Retrying in 2s... (Attempt ${i + 1}/${MAX_ATTEMPTS})`,
              );
              await new Promise((res) => setTimeout(res, 2000));
            } else {
              throw err;
            }
          }
        }

        if (!response) {
          throw lastError;
        }

        await response.wait();

        // toast.success('Permissions granted for trading!');
        return true;
      } catch (err: any) {
        let msg = typeof err === 'string' ? err : err?.message || 'Failed to set token approvals';

        if (msg.toLowerCase().includes('user rejected') || err?.code === 4001) {
          msg = 'Transaction rejected. Approvals are required to trade.';
        }
        setApprovalError(msg);
        console.error('[TokenApprovals] Set approvals failed:', err);
        // toast.error(msg);
        return false;
      } finally {
        setIsSettingApprovals(false);
      }
    },
    [client, user?.proxyWalletAddress, user?.tradingWalletKind],
  );

  /**
   * Convenience function: checks approvals and, if any are missing, sets them all.
   */
  const ensureAllTokenApprovals = useCallback(
    async (
      funderAddress: string,
      relayClientOverride?: typeof client,
      tradingWalletKindOverride?: TradingWalletKind,
    ): Promise<boolean> => {
      try {
        const status = await checkAllTokenApprovals(funderAddress);

        if (status.allApproved) {
          return true;
        }

        // toast.info(MESSAGES.TOAST.SETTING_TOKEN_APPROVALS);
        return await setAllTokenApprovals(
          relayClientOverride,
          tradingWalletKindOverride,
          funderAddress,
        );
      } catch (err) {
        const msg = err instanceof Error ? err.message : 'Approval check/set failed';
        console.error('[TokenApprovals] ensureAll failed:', msg);
        return false;
      }
    },
    [checkAllTokenApprovals, setAllTokenApprovals],
  );

  return {
    checkAllTokenApprovals,
    setAllTokenApprovals,
    ensureAllTokenApprovals,
    isCheckingApprovals,
    isSettingApprovals,
    approvalStatus,
    approvalError,
  };
}
